import { Fragment, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import type { MRT_ColumnDef } from "material-react-table"

import { BaseDrawer } from "../../../../../components/drawers/BaseDrawer"
import { Dropzone } from "../../../../../components/form/Dropzone"
import { BaseTable } from "../../../../../components/tables/BaseTable"
import { KonanAPI } from "../../../../../services/KonanAPI"
import { queryClient } from "../../../../../store/QueryClientStore"
import type { BulkCreateFeedbackRequest } from "../../../../../types/generated/api/BulkCreateFeedbackRequest"
import type { BulkCreateFeedbackResponse } from "../../../../../types/generated/api/BulkCreateFeedbackResponse"
import { generateTableColumnsFromData } from "../../../../../utils/genericHelpers"
import { convertFileToJson } from "../../../utils/helpers"
import { FEEDBACK_EXAMPLE_DATA } from "../Fixtures"

import styles from "./FeedbackSidebar.module.scss"

interface Props {
  onClose: () => void
}

/**
 * FeedbackSidebar Component
 *
 * A drawer component that allows users to upload CSV or JSON files containing
 * feedback data for predictions. Users can preview the uploaded file and submit
 * bulk feedback to update prediction results.
 *
 * @param props - Component props
 * @param props.onClose - Callback function to close the drawer
 */
export function FeedbackSidebar(props: Readonly<Props>): React.ReactElement {
  const { onClose } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("decisions")

  // File upload state
  const [acceptedFile, setAcceptedFile] = useState<File | undefined>(undefined)

  // CSV preview data (first 10 rows for table display)
  const [csvFilePreview, setCsvFilePreview] = useState<Array<Record<string, string | null>> | undefined>(undefined)

  // Table column definitions for the uploaded file preview
  const [csvFileColumns, setCsvFileColumns] = useState<MRT_ColumnDef[]>([])

  // Mutation for creating feedback in bulk
  // Handles success and error notifications, invalidates predictions cache
  const createFeedbackMutation = useMutation<
    AxiosResponse<BulkCreateFeedbackResponse>,
    AxiosError,
    BulkCreateFeedbackRequest & { project_uuid: string }
  >(KonanAPI.createFeedback, {
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries(["project-predictions"])

      onClose()
      NotificationUtils.toast(
        // TODO:: fix this in SynapseUI
        // @ts-expect-error - we want to return a fragment
        <Fragment>
          {t("projectDecisions.notifications.feedbackSubmitted")}
          <br />
          Successful: <span style={{ color: "var(--green-text-2)", display: "inline-block" }}>{data.success}</span>
          <br />
          Failed: <span style={{ color: "var(--red-text-2)", display: "inline-block" }}>{data.failure}</span>
        </Fragment>,
        {
          severity: "success",
        },
      )
    },
    onError: () => {
      queryClient.invalidateQueries(["project-predictions"])
      NotificationUtils.toast(t("projectDecisions.notifications.feedbackSubmissionFailed"), {
        severity: "error",
      })
    },
  })

  /**
   * Validates the structure of parsed JSON data
   * Throws an error if validation fails
   *
   * @param data - The parsed JSON data to validate
   * @throws Error if file structure is invalid
   */
  const validateFileStructure = (data: Array<Record<string, string | null>>): void => {
    // Validation: Check if file is empty
    if (data.length === 0) {
      setCsvFileColumns([])
      setCsvFilePreview([])
      setAcceptedFile(undefined)

      NotificationUtils.toast(t("feedbackSidebar.notifications.emptyFileError"), {
        severity: "error",
      })
      throw new Error("Empty file")
    }

    const firstRow = data[0] ?? {}

    // Validation: Must have exactly 2 columns
    if (Object.keys(firstRow).length !== 2) {
      NotificationUtils.toast(t("feedbackSidebar.notifications.invalidStructure.columns"), {
        severity: "error",
      })
      throw new Error("Invalid column count")
    }

    // Validation: Columns must be 'prediction_uuid' and 'feedback'
    if (!Object.keys(firstRow).every((key) => ["prediction_uuid", "feedback"].includes(key))) {
      NotificationUtils.toast(t("feedbackSidebar.notifications.invalidStructure.feedbackCols"), {
        severity: "error",
      })
      throw new Error("Invalid column names")
    }
  }

  /**
   * Handles file acceptance and validation
   *
   * Validates the uploaded CSV or JSON file structure and extracts
   * preview data for display. Ensures the file has exactly 2 columns:
   * prediction_uuid and feedback.
   *
   * @param file - The uploaded file to process
   */
  const handleFileAcceptance = async (file: File): Promise<void> => {
    try {
      const data = await convertFileToJson(file)
      validateFileStructure(data)

      // Extract first 10 rows for preview display
      const first10Rows = data.slice(0, 10)

      // Set preview data and columns for the table
      setCsvFilePreview(first10Rows)
      setCsvFileColumns(generateTableColumnsFromData(first10Rows))
      setAcceptedFile(file)
    } catch {
      NotificationUtils.toast(t("feedbackSidebar.notifications.emptyFileError"), {
        severity: "error",
      })
    }
  }

  /**
   * Handles the submission of feedback data to the API
   *
   * Reads the accepted file (CSV or JSON) and transforms it into the
   * required format before sending to the backend via mutation.
   */
  const handleCreateFeedback = async (): Promise<void> => {
    if (!acceptedFile) return

    const data = await convertFileToJson(acceptedFile)

    // Transform data and send to API
    createFeedbackMutation.mutate({
      project_uuid: projectId as string,
      feedback: data.map((row: Record<string, string | null>) => ({
        prediction_uuid: row?.prediction_uuid as string,
        target: row?.feedback as Record<string, unknown> | string,
      })),
    })
  }

  // Table column definitions for the example data display
  const columns: MRT_ColumnDef<{
    prediction_uuid: string
    feedback: string
  }>[] = [
    {
      header: t("feedbackSidebar.example.column1Header"),
      accessorKey: "prediction_uuid",
      Cell: ({ row }) => (
        <Typography variant="label-regular" variantColor={2}>
          {row.original.prediction_uuid}
        </Typography>
      ),
    },
    {
      header: t("feedbackSidebar.example.column2Header"),
      accessorKey: "feedback",
      Cell: ({ row }) => (
        <Typography variant="label-regular" variantColor={2}>
          {row.original.feedback}
        </Typography>
      ),
    },
  ]

  return (
    <BaseDrawer
      drawerProps={{
        open: true,
        // TODO:: refactor this to combine the two onClose functions
        onClose: !createFeedbackMutation.isLoading ? onClose : undefined,
      }}
      title="Feedback"
      onClose={onClose}
    >
      <Grid container direction={"column"} gap={2}>
        {/* File upload dropzone for CSV or JSON files */}
        <Dropzone
          uploadProgress={100}
          selectedFiles={acceptedFile ? [acceptedFile] : undefined}
          setSelectedFiles={(file: File[]) => {
            if (file[0]) handleFileAcceptance(file[0])
            else if (file.length === 0) setAcceptedFile(undefined)
          }}
          bottomText={t("feedbackSidebar.dropzone.bottomText")}
          titleHeader={{
            title: t("feedbackSidebar.dropzone.title"),
          }}
          extensions={{ "application/json": [".json"], "text/*": [".csv"] }}
          description={t("feedbackSidebar.dropzone.description")}
          uploadingCSV={createFeedbackMutation.isLoading}
        />

        {/* Table section: Shows either preview of uploaded file or example format */}
        <Grid item className={styles.exampleContainer}>
          {/* Show preview if file has been uploaded */}
          {csvFilePreview && csvFileColumns.length !== 0 ? (
            <Fragment>
              <Typography variant="label-bold">{t("feedbackSidebar.preview.title")}</Typography>

              <BaseTable
                newStyling
                disableToolbar
                disablePagination
                enableOrdering={false}
                data={csvFilePreview}
                columns={csvFileColumns}
                title={t("feedbackSidebar.preview.tableTitle")}
                pageSize={10}
                isLoading={false}
              />
            </Fragment>
          ) : (
            <Fragment>
              {/* Show example format if no file has been uploaded */}
              <Typography variant="label-bold">{t("feedbackSidebar.example.title")}</Typography>

              <Typography variant="label-regular" variantColor={2}>
                <Trans>{t("feedbackSidebar.example.description")}</Trans>
              </Typography>

              <BaseTable
                newStyling
                disableToolbar
                disablePagination
                enableOrdering={false}
                data={FEEDBACK_EXAMPLE_DATA.map((row) => ({
                  prediction_uuid: row.prediction_uuid,
                  feedback: t(row.feedback),
                }))}
                columns={(columns as MRT_ColumnDef[]) ?? []}
                title={t("feedbackSidebar.example.tableTitle")}
                isLoading={false}
              />
            </Fragment>
          )}
        </Grid>

        {/* Action buttons: Cancel and Upload */}
        <Grid container item className={styles.actions}>
          {/* Cancel button */}
          <Button variant="secondary" disabled={createFeedbackMutation.isLoading} onClick={() => onClose()}>
            {t("feedbackSidebar.buttons.cancel")}
          </Button>

          {/* Upload button: Disabled until file is selected */}
          <Button
            variant="primary"
            disabled={!acceptedFile || createFeedbackMutation.isLoading}
            onClick={handleCreateFeedback}
            isLoading={createFeedbackMutation.isLoading}
          >
            {t("feedbackSidebar.buttons.upload")}
          </Button>
        </Grid>
      </Grid>
    </BaseDrawer>
  )
}
